import type {
  DepositAccountResponse,
  DepositBankAccount,
  DepositCryptoAccount,
  DepositUpiAccount,
} from '@/types/Account';

export type DepositOptionKind = 'upi' | 'bank' | 'crypto';

type OptionBase = { id: string; label: string; min?: number; max?: number };

/**
 * One row in step 2: an admin account to pay into, from `/banker/show-account` (the same list
 * the /deposit page uses), so there's exactly one row per account admin has added. Limits are
 * in coins (1 coin = ₹1), crypto ones included.
 */
export type DepositOption =
  | (OptionBase & { kind: 'upi'; account: DepositUpiAccount })
  | (OptionBase & { kind: 'bank'; account: DepositBankAccount })
  | (OptionBase & { kind: 'crypto'; account: DepositCryptoAccount; rate?: number });

const toPositive = (value: string | number | null | undefined) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const isActive = (account: { status?: string }) => account.status?.toLowerCase() !== 'inactive';

export const buildDepositOptions = (data?: DepositAccountResponse): DepositOption[] => {
  return [
    ...(data?.Upi ?? []).filter(isActive).map(
      (account): DepositOption => ({
        kind: 'upi',
        id: `upi-${account.id}`,
        label: account.name?.trim() || account.upiId,
        min: toPositive(account.minAmount),
        max: toPositive(account.maxAmount),
        account,
      })
    ),
    ...(data?.Bank ?? []).filter(isActive).map(
      (account): DepositOption => ({
        kind: 'bank',
        id: `bank-${account.id}`,
        label: account.bankName?.trim() || 'Bank Transfer',
        min: toPositive(account.minDepositAmount),
        max: toPositive(account.maxDepositAmount),
        account,
      })
    ),
    ...(data?.Crypto ?? []).filter(isActive).map(
      (account): DepositOption => ({
        kind: 'crypto',
        id: `crypto-${account.id}`,
        label:
          account.network?.paymentOptions?.[0]?.displayName ||
          `USDT ${account.chain || account.network?.name || ''}`.trim(),
        min: toPositive(account.minAmount ?? account.minDepositAmount),
        max: toPositive(account.maxAmount ?? account.maxDepositAmount),
        rate: toPositive(account.conversionRate ?? account.network?.paymentOptions?.[0]?.usdRate),
        account,
      })
    ),
  ];
};

/** True when `amount` is inside the account's min / max (a missing limit doesn't restrict). */
export const acceptsAmount = (option: DepositOption, amount: number) =>
  amount > 0 && (!option.min || amount >= option.min) && (!option.max || amount <= option.max);

/** Step 2's rows: the options whose limits accept `amount`, in list order. */
export const getOptionsForAmount = (options: DepositOption[], amount: number) =>
  options.filter((option) => acceptsAmount(option, amount));

/**
 * The amounts some account accepts: the lowest minimum and the highest maximum. `max` is
 * undefined when an account has no upper limit.
 */
export const getAmountRange = (options: DepositOption[]) => {
  if (!options.length) return { min: undefined, max: undefined };
  const mins = options.map((option) => option.min ?? 1);
  const max = options.some((option) => !option.max)
    ? undefined
    : Math.max(...options.map((option) => option.max!));
  return { min: Math.min(...mins), max };
};

export const formatInr = (amount: number) => `₹${amount.toLocaleString('en-IN')}`;

/** "₹500 – ₹1,00,000", "From ₹500" or "" for an account's (or method's) limits. */
export const formatRange = (min?: number, max?: number) => {
  if (min && max) return `${formatInr(min)} – ${formatInr(max)}`;
  if (min) return `From ${formatInr(min)}`;
  if (max) return `Up to ${formatInr(max)}`;
  return '';
};
